import { useCallback, useEffect, useRef } from 'react';

/**
 * Card spotlight: a radial highlight that follows the pointer inside the
 * element, layered over its base background. Matches v1's [data-spot] cards.
 *
 * Returns a callback ref so it can be composed with other refs on one node.
 */
export function useSpotlight<T extends HTMLElement>(base = '#202428') {
    const nodeRef = useRef<T | null>(null);
    const cleanupRef = useRef<(() => void) | null>(null);

    const attach = useCallback(
        (node: T | null) => {
            cleanupRef.current?.();
            cleanupRef.current = null;
            nodeRef.current = node;

            if (!node) return;
            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

            const move = (e: PointerEvent) => {
                const r = node.getBoundingClientRect();
                const x = e.clientX - r.left;
                const y = e.clientY - r.top;
                node.style.background =
                    `radial-gradient(420px circle at ${x}px ${y}px, ` +
                    'rgba(230,201,143,0.10), rgba(3,147,231,0.06) 34%, transparent 62%), ' +
                    base;
            };

            const leave = () => {
                node.style.background = base;
            };

            node.addEventListener('pointermove', move);
            node.addEventListener('pointerleave', leave);

            cleanupRef.current = () => {
                node.removeEventListener('pointermove', move);
                node.removeEventListener('pointerleave', leave);
            };
        },
        [base]
    );

    useEffect(() => () => cleanupRef.current?.(), []);

    return attach;
}
